import ApiService from "@/shared/api/BaseApiService";
// import { POI } from "@/shared/types";  // wird nicht mehr gebraucht
import { Search } from "@mui/icons-material";
import { OutlinedInput, InputAdornment, IconButton, Alert, AlertTitle, Box, CircularProgress, Paper } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { Fragment, useState, KeyboardEvent, MouseEvent } from "react";

interface OrderResult {
  id: number;
  lat: number;
  lng: number;
  name: string;
  number: string;
}

interface ProjectGeocoderProps {
  setPosition: (position: { lat: number; lng: number }, label: string, something: boolean, id: number) => void;
  initialName?: string;
}

export default function ProjectGeocoder({ setPosition, initialName = "" }: ProjectGeocoderProps) {
  const [searchField, setSearchField] = useState(initialName);
  const [results, setResults] = useState<OrderResult[] | null>(null);

  const search = useMutation<OrderResult[], AxiosResponse, { search: string }>(ApiService.searchOrders(), {
    onSuccess: (dataUF) => {
      const data = (dataUF ?? []).filter((item) => item.lat !== 0 && item.lng !== 0 && item.lat !== null && item.lng !== null);
      setResults(data ?? []);

      // Optional: wenn nur ein Ergebnis, direkt auswählen
      if (data && data.length === 1) {
        const item = data[0];
        setPosition({ lat: item.lat, lng: item.lng }, `${item.city}: ${item.name} (${item.number})`, false, item.id);
      }
    },
  });

  const handleSearch = () => {
    const term = searchField.trim();
    if (term.length < 2) {
      // wie vorher: unter 2 Zeichen nichts tun / leeren
      setResults(null);
      return;
    }

    search.mutate({ search: term });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    // verhindert Fokus-Verlust auf dem Input
    event.preventDefault();
  };

  return (
    <Fragment>
      <OutlinedInput
        fullWidth
        size="small"
        sx={{ backgroundColor: "white" }}
        onKeyDown={handleKeyDown}
        endAdornment={
          <InputAdornment position="end">
            <IconButton onClick={handleSearch} onMouseDown={handleMouseDown} edge="end">
              {search.isPending ? <CircularProgress size={18} /> : <Search />}
            </IconButton>
          </InputAdornment>
        }
        placeholder="Projekt suchen..."
        value={searchField}
        onChange={(e) => setSearchField(e.target.value)}
      />

      {search.isError && (
        <Alert severity="error" sx={{ mt: "10px" }}>
          <AlertTitle>Fehler bei der Suche</AlertTitle>
          Bitte versuche es später erneut.
        </Alert>
      )}

      {!Array.isArray(results) ? null : (
        <Paper variant="outlined" sx={{ mt: "10px", maxHeight: "300px", overflow: "auto" }}>
          {search.isPending ? (
            <Box
              sx={{
                minHeight: "80px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : results.length === 0 ? (
            <Alert severity="info">
              <AlertTitle>Nichts gefunden.</AlertTitle>
            </Alert>
          ) : (
            results.map((item, i) => (
              <Box
                key={i}
                onClick={() => setPosition({ lat: item.lat, lng: item.lng }, `${item.name} (${item.number})`, false, item.id)}
                sx={{
                  borderTop: "1px solid #ccc",
                  cursor: "pointer",
                  "&:first-child": { borderTop: "none" },
                  "&:hover": { background: "#f9f9ff" },
                }}
              >
                <Box sx={{ padding: "5px" }}>
                  {item.city}: {item.name} ({item.number})
                </Box>
              </Box>
            ))
          )}
        </Paper>
      )}
    </Fragment>
  );
}
